import BtnCustom from "components/common/btn-custom";
import { useFormik } from "formik";
import React, { useContext } from "react";
import { Form, Modal, ModalBody } from "reactstrap";
import { ErrorToaster } from "components/common/toast";
import handleError from "utils/handleError";
import CustomInputField from "components/common/CustomInputField";
import { UpdateUserAdminAPI } from "network/user";
import { UpdateUserResellerAPI } from "network/user";
import { UserContextProvider } from "context/UserContext";
import { roleOptions } from "constant/SelectOptions";
import { successToaster } from "components/common/toast";
import CustomPaginatedSelect from "components/common/CustomPaginatedSelect";
import { EditUserSchema } from "components/schema/EditUserSchema";
import ModulesPicker from "components/user/ModulesPicker";
import { useTranslation } from "react-i18next";

const EditUserModalFrom = ({
  open,
  setOpen,
  singleUser,
  setUserData,
  loadResellerOptions,
}) => {
  const { t } = useTranslation("users");
  const { user } = useContext(UserContextProvider);
  const isAdmin = ["admin", "super_admin"].includes(user?.role?.toLowerCase());

  const closeModal = () => {
    setOpen(!setOpen);
  };

  const {
    errors,
    values,
    handleBlur,
    handleChange,
    handleSubmit,
    touched,
    isSubmitting,
    setFieldTouched,
    setFieldValue,
  } = useFormik({
    initialValues: {
      restaurantName: singleUser?.restaurant_name || "",
      modules: singleUser?.modules || [],
      firstName: singleUser?.first_name || "",
      lastName: singleUser?.last_name || "",
      email: singleUser?.email || "",
      phone: singleUser?.phone_number || "",
      role: singleUser?.role
        ? { value: singleUser?.role, label: singleUser?.role }
        : "",
      reseller: singleUser?.reseller?.uuid
        ? {
            value: singleUser?.reseller?.uuid,
            label:
              singleUser?.reseller?.first_name +
              " " +
              singleUser?.reseller?.last_name,
          }
        : "",
    },

    enableReinitialize: true,
    validationSchema: EditUserSchema,
    validate: (vals) => {
      const found = {};
      const isUser = (vals.role?.value || "").toLowerCase() === "user";
      if (isAdmin && !vals.restaurantName.trim()) {
        found.restaurantName = t("form.restaurantRequired");
      }
      if (isUser && vals.modules.length === 0) {
        found.modules = t("form.modulesRequired");
      }
      return found;
    },

    onSubmit: async (values) => {
      try {
        const formData = new FormData();
        if (isAdmin) {
          formData.append("restaurant_name", values.restaurantName.trim());
        }
        if ((values?.role?.value || "").toLowerCase() === "user") {
          values.modules.forEach((code) => formData.append("modules", code));
        }
        formData.append("first_name", values.firstName);
        formData.append("last_name", values.lastName);
        formData.append("email", values.email);
        formData.append("phone_number", values.phone);
        formData.append("role", values?.role?.value);
        if (values?.reseller?.value) {
          formData.append("reseller", values.reseller?.value);
        }
        let response;
        if (
          user?.role?.toLowerCase() === "admin" ||
          user?.role?.toLowerCase() === "super_admin"
        ) {
          response = await UpdateUserAdminAPI(singleUser?.uuid, formData);
        } else if (user?.role?.toLowerCase() === "reseller") {
          response = await UpdateUserResellerAPI(singleUser?.uuid, formData);
        }
        if (response?.success) {
          setUserData((prev) =>
            prev.map((item) =>
              item?.uuid === response?.data?.uuid ? response.data : item
            )
          );
          successToaster(t("form.updated"));
          closeModal();
        }
      } catch (error) {
        closeModal();
        ErrorToaster(handleError(error?.status_code, error?.message));
      }
    },
  });

  return (
    <Modal isOpen={open} toggle={closeModal} className="userEditModal">
      <div className="modal-header justify-content-center">
        <button
          aria-hidden={true}
          className="close"
          data-dismiss="modal"
          type="button"
          onClick={closeModal}
        >
          <i className="tim-icons icon-simple-remove pointer" />
        </button>
        <h3 className="title title-up text-capitalize">{t("form.updateUserTitle")}</h3>
      </div>

      <Form onSubmit={handleSubmit}>
        <ModalBody>
          {isAdmin && (
            <CustomInputField
              label={t("form.restaurantName")}
              name="restaurantName"
              placeholder={t("form.restaurantName")}
              type="text"
              errors={errors}
              touched={touched}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values}
            />
          )}
          <CustomInputField
            label={t("form.firstName")}
            name="firstName"
            placeholder={t("form.firstName")}
            type="text"
            errors={errors}
            touched={touched}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values}
          />
          <CustomInputField
            label={t("form.lastName")}
            name="lastName"
            placeholder={t("form.lastName")}
            type="text"
            errors={errors}
            touched={touched}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values}
          />
          <CustomInputField
            label={t("form.phone")}
            name="phone"
            placeholder={t("form.phonePlaceholder")}
            type="text"
            errors={errors}
            touched={touched}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values}
          />
          <CustomInputField
            label={t("form.email")}
            name="email"
            placeholder={t("form.email")}
            type="email"
            errors={errors}
            touched={touched}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values}
          />

          {(user?.role?.toLowerCase() === "admin" ||
            user?.role?.toLowerCase() === "super_admin") && (
            <CustomPaginatedSelect
              label={t("form.selectRole")}
              name="role"
              placeholder={t("form.selectRole")}
              staticOptions={roleOptions || []}
              value={values.role}
              onChange={(value) => setFieldValue("role", value)}
              onBlur={() => setFieldTouched("role", true)}
              touched={touched}
              errors={errors}
              colorCode="#9A9A9A"
              isSearchable={false}
            />
          )}
          {(user?.role?.toLowerCase() === "admin" ||
            user?.role?.toLowerCase() === "super_admin") && (
            <CustomPaginatedSelect
              label={t("form.selectReseller")}
              name="reseller"
              placeholder={t("form.selectReseller")}
              disabled={values?.role?.value?.toLowerCase() !== "user"}
              value={values.reseller}
              onChange={(value) => setFieldValue("reseller", value)}
              onBlur={() => setFieldTouched("reseller", true)}
              touched={touched}
              errors={errors}
              loadOptions={loadResellerOptions}
              colorCode="#9A9A9A"
            />
          )}
          {(values?.role?.value || "").toLowerCase() === "user" && (
            <ModulesPicker
              value={values.modules}
              onChange={(next) => setFieldValue("modules", next)}
              error={errors.modules}
            />
          )}
        </ModalBody>
        <div className="modal-footer">
          <BtnCustom
            className="btn-round w-50"
            color="primary"
            size="lg"
            type="submit"
            isLoading={isSubmitting}
            submittingText={t("form.updating")}
          >
            {t("form.update")}
          </BtnCustom>
        </div>
      </Form>
    </Modal>
  );
};

export default EditUserModalFrom;
